/* assets/gtender-opening.js — GTENDER-OPENING-RUNTIME-01
 *
 * Canonical lifecycle owner for GTender opening overlay.
 * Mirrors the exact architecture of assets/littleg-kid-opening.js.
 *
 * State states (on [data-gtender-opening]):
 *   waiting -> entering -> ready
 *
 * Responsibility Note:
 * - Owns opening state transition & background inert locking only.
 * - Does not own search, filters, bookmarks, account or checkout.
 * - Does not mutate URL.
 * - Persists "seen" flag to localStorage for returning user variant projection.
 */

(function (window, document) {
  'use strict';

  const VERSION = '1.2.2';
  const OPENING_SELECTOR = '[data-gtender-opening]';
  const ENTER_SELECTOR = '[data-gtender-opening-enter]';
  const STATE_ATTR = 'data-gtender-opening-state';
  const VARIANT_ATTR = 'data-gtender-opening-variant';
  const INERT_ATTR = 'data-gtender-opening-inert-owned';
  const LOCK_CLASS = 'gtender-opening-lock';
  const SEEN_KEY = 'gtender_opening_seen';
  const EVENT_PREFIX = 'gtender:opening:';
  const EXIT_DURATION_MS = 900;
  const SAFETY_MS = 5000;

  if (window.__GTENDER_OPENING__ === VERSION) return;
  window.__GTENDER_OPENING__ = VERSION;

  /* Utility: Query selector all as Array */
  function qa(root, selector) {
    return root ? Array.prototype.slice.call(root.querySelectorAll(selector)) : [];
  }

  /* Utility: Reduced motion preference check */
  const motionPreference = (() => {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)');
    } catch (_) {
      return null;
    }
  })();

  function reducedMotion() {
    return Boolean(motionPreference && motionPreference.matches);
  }

  /* Utility: LocalStorage seen status */
  function hasSeen() {
    try {
      return window.localStorage.getItem(SEEN_KEY) === '1';
    } catch (_) {
      return false;
    }
  }

  function markSeen() {
    try {
      window.localStorage.setItem(SEEN_KEY, '1');
    } catch (_) {}
  }

  /* Custom Event Dispatcher */
  function emit(name, opening, state) {
    try {
      document.dispatchEvent(
        new CustomEvent(EVENT_PREFIX + name, {
          bubbles: true,
          detail: {
            state: state,
            variant: opening.getAttribute(VARIANT_ATTR) || ''
          }
        })
      );
    } catch (_) {}
  }

  /* Variant projector (first-use vs returning) */
  function projectVariant(opening) {
    const variant = hasSeen() ? 'returning' : 'first-use';
    opening.setAttribute(VARIANT_ATTR, variant);

    qa(opening, '[data-gtender-opening-copy]').forEach(function (node) {
      node.hidden = node.getAttribute('data-gtender-opening-copy') !== variant;
    });

    qa(opening, '[data-gtender-opening-enter-label]').forEach(function (node) {
      node.hidden = node.getAttribute('data-gtender-opening-enter-label') !== variant;
    });
  }

  /* Lock all background elements except opening's ancestor chain */
  function lockBackground(opening) {
    let node = opening;

    while (node && node !== document.body && node.parentElement) {
      Array.prototype.slice.call(node.parentElement.children).forEach(function (sibling) {
        if (sibling === node || sibling.inert === true) return;
        if (sibling.tagName === 'SCRIPT' || sibling.tagName === 'STYLE') return;
        sibling.inert = true;
        sibling.setAttribute(INERT_ATTR, '');
      });
      node = node.parentElement;
    }

    document.documentElement.classList.add(LOCK_CLASS);
  }

  /* Unlock background elements */
  function unlockBackground() {
    qa(document, '[' + INERT_ATTR + ']').forEach(function (node) {
      node.inert = false;
      node.removeAttribute(INERT_ATTR);
    });

    document.documentElement.classList.remove(LOCK_CLASS);
  }

  /* Focus management helpers */
  function focusNode(node) {
    if (!node) return;
    try {
      node.focus({ preventScroll: true });
    } catch (_) {
      node.focus();
    }
  }

  function focusEnter(opening) {
    const button = opening.querySelector(ENTER_SELECTOR);
    if (!button) return;
    window.requestAnimationFrame(function () {
      const panel = opening.querySelector('.gtender-opening__panel');
      if (panel) panel.setAttribute('tabindex', '-1');
      focusNode(panel || button);
    });
  }

  function focusTarget(opening) {
    const selector = opening.getAttribute('data-gtender-opening-focus') || '';
    let target = null;

    if (selector) {
      try {
        target = document.querySelector(selector);
      } catch (_) {
        target = null;
      }
    }

    if (!target) {
      target = document.querySelector('main h1, h1:not([id^="GTenderOpeningTitle"])');
      if (target && !target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
    }

    if (window.matchMedia('(max-width: 767px)').matches) {
      const heading = document.querySelector('.gtender-feed-header h2');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        target = heading;
      }
    }
    focusNode(target);
  }

  /* Final state resolution: ready */
  function finish(opening) {
    if (!opening || !opening.isConnected) return;
    if (opening.getAttribute(STATE_ATTR) === 'ready') return;

    window.clearTimeout(opening.__gtenderOpeningExitTimer);
    window.clearTimeout(opening.__gtenderOpeningSafetyTimer);

    opening.setAttribute(STATE_ATTR, 'ready');
    opening.hidden = true;
    opening.setAttribute('aria-hidden', 'true');

    unlockBackground();
    markSeen();
    focusTarget(opening);
    emit('ready', opening, 'ready');
  }

  /* Transition state: waiting -> entering */
  function enter(opening, trigger) {
    if (!opening || opening.getAttribute(STATE_ATTR) !== 'waiting') return;

    if (trigger) {
      trigger.disabled = true;
      trigger.setAttribute('aria-disabled', 'true');
    }

    opening.setAttribute(STATE_ATTR, 'entering');
    emit('enter', opening, 'entering');

    const duration = reducedMotion() ? 0 : EXIT_DURATION_MS;

    window.clearTimeout(opening.__gtenderOpeningExitTimer);
    opening.__gtenderOpeningExitTimer = window.setTimeout(function () {
      finish(opening);
    }, duration);

    /* Safety fallback timer */
    window.clearTimeout(opening.__gtenderOpeningSafetyTimer);
    opening.__gtenderOpeningSafetyTimer = window.setTimeout(function () {
      if (opening.getAttribute(STATE_ATTR) === 'entering') finish(opening);
    }, SAFETY_MS);
  }

  /* Mount opening instance */
  function mount(opening) {
    if (!opening || opening.__gtenderOpeningVersion === VERSION) return;

    if (opening.getAttribute(STATE_ATTR) !== 'waiting') return;

    const trigger = opening.querySelector(ENTER_SELECTOR);
    if (!trigger) return;

    opening.__gtenderOpeningVersion = VERSION;
    projectVariant(opening);

    if (hasSeen()) {
      finish(opening);
      return;
    }

    opening.hidden = false;
    opening.removeAttribute('aria-hidden');
    opening.setAttribute('data-gtender-opening-layout', 'modal');
    opening.setAttribute('role', 'dialog');
    opening.setAttribute('aria-modal', 'true');
    lockBackground(opening);
    focusEnter(opening);

    trigger.addEventListener('click', function () {
      enter(opening, trigger);
    });

    opening.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        enter(opening, trigger);
      }
      if (event.key === 'Tab' && opening.getAttribute('data-gtender-opening-layout') === 'modal') {
        event.preventDefault();
        focusNode(trigger);
      }
    });

  }

  function mountAll() {
    qa(document, OPENING_SELECTOR).forEach(mount);
  }

  /* Shopify Theme Editor Lifecycle Listeners */
  document.addEventListener('shopify:section:unload', function (event) {
    qa(event.target, OPENING_SELECTOR).forEach(function (opening) {
      window.clearTimeout(opening.__gtenderOpeningExitTimer);
      window.clearTimeout(opening.__gtenderOpeningSafetyTimer);
      opening.__gtenderOpeningVersion = null;
    });
    unlockBackground();
  });

  document.addEventListener('shopify:section:load', mountAll);

  /* DOM Readiness Initialization */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll, { once: true });
  } else {
    mountAll();
  }

  /* Global Public API */
  window.GTenderOpening = Object.freeze({
    version: VERSION,
    mount: mountAll,
    enter: function () {
      const opening = document.querySelector(OPENING_SELECTOR);
      if (opening) enter(opening, opening.querySelector(ENTER_SELECTOR));
    },
    state: function () {
      const opening = document.querySelector(OPENING_SELECTOR);
      return opening ? opening.getAttribute(STATE_ATTR) || '' : '';
    },
    reset: function () {
      try {
        window.localStorage.removeItem(SEEN_KEY);
      } catch (_) {}
    }
  });
})(window, document);
